import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { getAdminAccessToken } from '../utils/authToken';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Common icons for KB categories
const ICON_OPTIONS = [
  { value: '', label: 'None' },
  { value: '📚', label: '📚 Books' },
  { value: '🚀', label: '🚀 Getting Started' },
  { value: '⚙️', label: '⚙️ Settings' },
  { value: '🎨', label: '🎨 Design' },
  { value: '💳', label: '💳 Billing' },
  { value: '🔧', label: '🔧 Troubleshooting' },
  { value: '📱', label: '📱 Mobile' },
  { value: '🌐', label: '🌐 Domain' },
  { value: '🔒', label: '🔒 Security' },
  { value: '📊', label: '📊 Analytics' },
  { value: '🤖', label: '🤖 AI Features' },
  { value: '📧', label: '📧 Email' },
  { value: '🔌', label: '🔌 Integrations' },
  { value: '❓', label: '❓ FAQ' }
];

const KnowledgeBaseCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIcon, setFormIcon] = useState('');
  const [formDisplayOrder, setFormDisplayOrder] = useState(0);
  const [formSaving, setFormSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = getAdminAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/kb/categories`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(response.data.data?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormName('');
    setFormSlug('');
    setFormDescription('');
    setFormIcon('');
    setFormDisplayOrder(0);
    setEditingId(null);
    setShowNewForm(false);
  };

  const startEdit = (category) => {
    setFormName(category.name);
    setFormSlug(category.slug);
    setFormDescription(category.description || '');
    setFormIcon(category.icon || '');
    setFormDisplayOrder(category.display_order || 0);
    setEditingId(category.id);
    setShowNewForm(false);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      setFormSaving(true);
      const token = getAdminAccessToken();

      const categoryData = {
        name: formName,
        slug: formSlug || undefined,
        description: formDescription,
        icon: formIcon,
        displayOrder: parseInt(formDisplayOrder) || 0
      };

      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/api/admin/kb/categories/${editingId}`,
          categoryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/admin/kb/categories`,
          categoryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category created');
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.error || 'Failed to save category');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      const token = getAdminAccessToken();
      await axios.delete(
        `${API_BASE_URL}/api/admin/kb/categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Category deleted');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/knowledge-base"
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BookOpenIcon className="w-8 h-8 text-primary-600" />
                KB Categories
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Organize your knowledge base articles with categories
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchCategories}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowNewForm(true);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              New Category
            </button>
          </div>
        </div>

        {/* New Category Form */}
        {showNewForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">New Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Category name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (auto-generated if empty)
                </label>
                <input
                  type="text"
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="category-slug"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <select
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {ICON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formDisplayOrder}
                  onChange={(e) => setFormDisplayOrder(e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief description of this category..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={formSaving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {formSaving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Create Category
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No categories yet. Create your first category to get started.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Articles
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id}>
                    {editingId === category.id ? (
                      // Editing row
                      <>
                        <td className="px-6 py-4">
                          <input
                            type="number"
                            value={formDisplayOrder}
                            onChange={(e) => setFormDisplayOrder(e.target.value)}
                            min="0"
                            className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <select
                              value={formIcon}
                              onChange={(e) => setFormIcon(e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {ICON_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.value || '—'}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={formName}
                              onChange={(e) => setFormName(e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            value={formSlug}
                            onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {category.article_count || 0}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={resetForm}
                              className="p-1 text-gray-400 hover:text-gray-600 rounded"
                              title="Cancel"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={handleSave}
                              disabled={formSaving}
                              className="p-1 text-green-600 hover:text-green-700 rounded disabled:opacity-50"
                              title="Save"
                            >
                              <CheckIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // Display row
                      <>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {category.display_order || 0}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {category.icon && (
                              <span className="text-lg">{category.icon}</span>
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{category.name}</div>
                              {category.description && (
                                <div className="text-sm text-gray-500">{category.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {category.slug}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {category.article_count || 0}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => startEdit(category)}
                              className="p-1 text-gray-400 hover:text-primary-600 rounded"
                              title="Edit"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(category.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Category?
              </h3>
              <p className="text-gray-600 mb-6">
                This will remove the category from all associated articles. The articles themselves will not be deleted.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KnowledgeBaseCategories;

